import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Luggage as LuggageIcon,
  Map as MapIcon
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { useTrip } from '../../contexts/TripContext';
import { useWebSocketChat } from '../../contexts/WebSocketChatContext';
import chatService from '../../services/ChatService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';

interface NewChatDialogProps {
   open: boolean;
   onClose: () => void;
}

interface UserInfo {
   id: string;
   name: string;
   email: string;
   photoURL?: string;
}

interface TabPanelProps {
   children?: React.ReactNode;
   index: number;
   value: number;
}

interface Trip {
   id: string;
   title: string;
   primaryDestination: {
      name: string;
   };
   coverPhotoUrl?: string;
   coverImageUrl?: string;
}

// Tab Panel component
const TabPanel = (props: TabPanelProps) => {
   const { children, value, index, ...other } = props;

   return (
      <div
         role='tabpanel'
         hidden={value !== index}
         id={`tabpanel-${index}`}
         aria-labelledby={`tab-${index}`}
         {...other}
         style={{ padding: '16px 0' }}
      >
         {value === index && <>{children}</>}
      </div>
   );
};

const NewChatDialog: React.FC<NewChatDialogProps> = ({ open, onClose }) => {
   const [tabValue, setTabValue] = useState(0);
   const [searchQuery, setSearchQuery] = useState('');
   const [loading, setLoading] = useState(false);
   const [users, setUsers] = useState<UserInfo[]>([]);
   const [error, setError] = useState<string | null>(null);
   const { currentUser } = useUser();
   const { trips } = useTrip();
   const { createOneOnOneChat, createTripGroupChat, refreshChannels } = useWebSocketChat();

   // Handle tab change
   const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
      setTabValue(newValue);
   };

   // Load trip members that the user can chat with
   useEffect(() => {
      const loadChatEligibleUsers = async () => {
         if (!currentUser) return;

         setLoading(true);
         setError(null);

         try {
            // Get IDs of users the current user can chat with
            const eligibleUserIds = await chatService.getChatEligibleUsers(currentUser.uid);

            if (eligibleUserIds.length === 0) {
               setUsers([]);
               setLoading(false);
               return;
            }

            // Fetch user details
            const usersData: UserInfo[] = [];

            // Get user details from Firebase Auth via Firestore profile documents
            // We need to batch this because 'in' queries can only handle 10 items at a time
            const batchSize = 10;

            for (let i = 0; i < eligibleUserIds.length; i += batchSize) {
               const batch = eligibleUserIds.slice(i, i + batchSize);

               const usersQuery = query(
                  collection(db, 'userProfiles'),
                  where('userId', 'in', batch)
               );

               const usersSnapshot = await getDocs(usersQuery);

               usersSnapshot.forEach((doc) => {
                  const userData = doc.data();
                  usersData.push({
                     id: userData.userId,
                     name: userData.displayName || 'Unknown User',
                     email: userData.email || '',
                     photoURL: userData.photoURL,
                  });
               });
            }

            setUsers(usersData);
         } catch (err) {
            console.error('Error loading chat eligible users:', err);
            setError('Could not load users. Please try again.');
         } finally {
            setLoading(false);
         }
      };

      if (open) {
         loadChatEligibleUsers();
      }
   }, [currentUser, open]);

   // Filter users based on search query
   const filteredUsers = users.filter(
      (user) =>
         user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
         user.email.toLowerCase().includes(searchQuery.toLowerCase())
   );

   // Start a direct chat with a user
   const handleStartDirectChat = async (userId: string) => {
      if (!currentUser) return;

      setLoading(true);

      try {
         // First check if the user can chat with this person
         const canChat = await chatService.canChatWithUser(currentUser.uid, userId);

         if (!canChat) {
            setError(
               'You can only chat with members of your trips who have accepted your invitation.'
            );
            setLoading(false);
            return;
         }

         // Create or get the chat channel
         await createOneOnOneChat(userId);

         // Refresh channels list
         refreshChannels();

         // Close dialog
         onClose();
      } catch (err) {
         console.error('Error starting direct chat:', err);
         setError('Failed to start chat. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   // Create a group chat for a trip
   const handleCreateTripChat = async (tripId: string, tripTitle: string) => {
      if (!currentUser) return;

      setLoading(true);

      try {
         // Create group chat for trip
         await createTripGroupChat(tripId, tripTitle);

         // Refresh channels list
         refreshChannels();

         // Close dialog
         onClose();
      } catch (err) {
         console.error('Error creating trip chat:', err);
         setError('Failed to create trip chat. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   return (
      <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
         <DialogTitle>Start a New Conversation</DialogTitle>

         <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant='fullWidth'
            sx={{ borderBottom: 1, borderColor: 'divider' }}
         >
            <Tab
               icon={<PersonIcon />}
               label='Direct Message'
               id='tab-0'
               aria-controls='tabpanel-0'
            />
            <Tab icon={<GroupIcon />} label='Trip Chat' id='tab-1' aria-controls='tabpanel-1' />
         </Tabs>

         <DialogContent dividers>
            {error && (
               <Alert severity='error' sx={{ mb: 2 }}>
                  {error}
               </Alert>
            )}

            {/* Direct Messages Tab */}
            <TabPanel value={tabValue} index={0}>
               <TextField
                  fullWidth
                  label='Search trip members'
                  placeholder='Search by name or email'
                  variant='outlined'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                     startAdornment: <SearchIcon color='action' sx={{ mr: 1 }} />,
                  }}
                  sx={{ mb: 2 }}
               />

               <Box sx={{ height: 300, overflow: 'auto' }}>
                  {loading ? (
                     <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                     </Box>
                  ) : filteredUsers.length === 0 ? (
                     <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography color='text.secondary'>
                           {users.length === 0
                              ? 'No eligible users found. You can only chat with users who have accepted your trip invitations.'
                              : 'No users match your search criteria.'}
                        </Typography>
                     </Box>
                  ) : (
                     <List>
                        {filteredUsers.map((user) => (
                           <ListItem
                              key={user.id}
                              button
                              onClick={() => handleStartDirectChat(user.id)}
                           >
                              <ListItemAvatar>
                                 <Avatar src={user.photoURL}>{user.name.charAt(0)}</Avatar>
                              </ListItemAvatar>
                              <ListItemText primary={user.name} secondary={user.email} />
                           </ListItem>
                        ))}
                     </List>
                  )}
               </Box>
            </TabPanel>

            {/* Trip Chats Tab */}
            <TabPanel value={tabValue} index={1}>
               <Typography variant='body2' paragraph>
                  Create a group chat for one of your trips. All members of the trip will be added
                  to the conversation.
               </Typography>

               <Box sx={{ height: 300, overflow: 'auto' }}>
                  {trips.length === 0 ? (
                     <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography color='text.secondary'>
                           You don't have any trips yet. Create a trip first to start a trip chat.
                        </Typography>
                     </Box>
                  ) : (
                     <List>
                        {trips.map((trip) => (
                           <ListItem
                              key={trip.id}
                              button
                              onClick={() => handleCreateTripChat(trip.id, trip.title)}
                           >
                              <ListItemAvatar>
                                 <Avatar
                                    src={(trip as any).coverPhotoUrl || (trip as any).coverImageUrl}
                                 >
                                    <MapIcon />
                                 </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                 primary={trip.title}
                                 secondary={
                                    <Box>
                                       <Typography variant='body2' component='span'>
                                          {new Date(trip.startDate).toLocaleDateString()} -{' '}
                                          {new Date(trip.endDate).toLocaleDateString()}
                                       </Typography>
                                       <Chip
                                          size='small'
                                          label='Group Chat'
                                          color='primary'
                                          variant='outlined'
                                          icon={<GroupIcon />}
                                          sx={{ ml: 1 }}
                                       />
                                    </Box>
                                 }
                              />
                           </ListItem>
                        ))}
                     </List>
                  )}
               </Box>
            </TabPanel>
         </DialogContent>

         <DialogActions>
            <Button onClick={onClose} color='inherit'>
               Cancel
            </Button>
         </DialogActions>
      </Dialog>
   );
};

export default NewChatDialog; 